import * as React from 'react';

import { TextField, DatePicker } from '@rematter/ui-web';
import { useAtom } from 'jotai';

import {
  anyCalendarDateToCalendarDate,
  toISODateString,
} from '../../../../shared/utils/dateTime';
import * as state from '../../_state/bookingForm.state';
import { SalesOrdersMultiSelect } from '../_shared/SalesOrdersMultiSelect';

import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { SelectFacility } from '~/shared/components/select-fields/SelectFacility';
import { SelectPort } from '~/shared/components/select-fields/SelectPort';
import { SelectTerminal } from '~/shared/components/select-fields/SelectTerminal';
import { maybeParseCalendarDate } from '~/shared/utils/dateTime';

interface Props {
  validationErrors: Record<string, string>;
}

export const BookingWizardStepBasicInfo: React.FC<Props> = ({
  validationErrors,
}) => {
  const t = useTranslations();
  const [formData, setFormData] = useAtom(state.$bookingForm);

  const handleFieldChange =
    <K extends keyof typeof formData>(field: K) =>
    (value: (typeof formData)[K]) => {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

  const handleDateChange =
    (field: keyof typeof formData) => (value: string | null) => {
      setFormData((prev) => ({
        ...prev,
        [field]: value ? maybeParseCalendarDate(value) : undefined,
      }));
    };

  const handleFacilityChange = React.useCallback(
    (facilityId: string | undefined) => {
      setFormData((prev) => ({
        ...prev,
        facilityId: facilityId ?? undefined,
      }));
    },
    [setFormData],
  );

  return (
    <div className="@BookingWizardStepBasicInfo flex flex-col gap-6">
      <div className="space-y-4">
        <h3 className="text-base font-semibold text-neutral/90">
          {t.bookingsView.form.sections.basicInfo}
        </h3>
        <div className="grid grid-cols-1 gap-4">
          <SelectFacility
            value={formData.facilityId}
            onChange={handleFacilityChange}
            label={t.bookingsView.form.fields.facility}
            autoSelectFirstOption
            isRequired
            errorMessage={validationErrors.facilityId}
          />
          <TextField
            label={t.bookingsView.form.fields.bookingNumber}
            value={formData.bookingNumber || ''}
            onChange={handleFieldChange('bookingNumber')}
            placeholder={t.common.placeholders.generateNumber}
            isRequired
          />
        </div>
      </div>
      <div className="grid max-w-[560px] grid-cols-1">
        <SalesOrdersMultiSelect
          onSelectionChange={handleFieldChange('salesOrders')}
          selectedEntries={formData.salesOrders}
          className="w-full"
        />
      </div>

      {/* Freight Info Section */}
      <div className="flex flex-col gap-4">
        <h3 className="text-base font-semibold text-neutral/90">
          {t.bookingsView.form.sections.freightInfo}
        </h3>

        <div className="grid grid-cols-2 gap-4">
          <TextField
            label={t.bookingsView.form.fields.steamshipLine}
            value={formData.steamshipLine || ''}
            onChange={handleFieldChange('steamshipLine')}
            isRequired
            errorMessage={validationErrors.steamshipLine}
          />

          <TextField
            label={t.bookingsView.form.fields.vesselName}
            value={formData.vesselName || ''}
            onChange={handleFieldChange('vesselName')}
            isRequired
            errorMessage={validationErrors.vesselName}
          />

          <SelectPort
            value={formData.loadingPortId}
            onChange={handleFieldChange('loadingPortId')}
            label={t.bookingsView.form.fields.loadingPort}
            isRequired
            errorMessage={validationErrors.loadingPort}
          />

          <SelectTerminal
            value={formData.pickupTerminalId}
            onChange={handleFieldChange('pickupTerminalId')}
            label={t.bookingsView.form.fields.pickupTerminal}
            portId={formData.loadingPortId}
            selectedTerminal={formData.pickupTerminal}
            key={`pickup-terminal-${formData.loadingPortId}`}
            isRequired
          />

          <SelectPort
            value={formData.destinationPortId}
            onChange={handleFieldChange('destinationPortId')}
            label={t.bookingsView.form.fields.destinationPort}
            isRequired
            errorMessage={validationErrors.destinationPort}
          />

          <SelectTerminal
            value={formData.dropoffTerminalId}
            onChange={handleFieldChange('dropoffTerminalId')}
            errorMessage={validationErrors.dropoffTerminal}
            label={t.bookingsView.form.fields.dropoffTerminal}
            portId={formData.destinationPortId}
            selectedTerminal={formData.dropoffTerminal}
            isRequired
          />
          <TextField
            label={t.bookingsView.form.fields.oceanFreight}
            prefix="$"
            value={formData.oceanFreight || ''}
            onChange={handleFieldChange('oceanFreight')}
          />
        </div>
      </div>

      {/* Shipping Dates Section */}
      <div className="space-y-4">
        <h3 className="text-base font-semibold text-neutral/90">
          {t.bookingsView.form.sections.shippingDates}
        </h3>

        <div className="grid grid-cols-2 gap-4">
          <DatePicker
            label={t.bookingsView.form.fields.earlyReturnDate}
            value={anyCalendarDateToCalendarDate(formData.earlyReturnDate)}
            onChange={(date) =>
              handleDateChange('earlyReturnDate')(
                date ? toISODateString(date) : null,
              )
            }
          />

          <DatePicker
            label={t.bookingsView.form.fields.cutOffDate}
            value={anyCalendarDateToCalendarDate(formData.cutOffDate)}
            onChange={(date) =>
              handleDateChange('cutOffDate')(
                date ? toISODateString(date) : null,
              )
            }
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <DatePicker
            label={t.bookingsView.form.fields.vesselDepartureDate}
            value={anyCalendarDateToCalendarDate(formData.vesselDepartureDate)}
            onChange={(date) =>
              handleDateChange('vesselDepartureDate')(
                date ? toISODateString(date) : null,
              )
            }
          />

          <DatePicker
            label={t.bookingsView.form.fields.vesselArrivalDate}
            value={anyCalendarDateToCalendarDate(formData.vesselArrivalDate)}
            onChange={(date) =>
              handleDateChange('vesselArrivalDate')(
                date ? toISODateString(date) : null,
              )
            }
          />
        </div>
      </div>
    </div>
  );
};
